import { useMemo, useState } from 'react';
import { Bug, Check, Search, X } from 'lucide-react';
import { countUnnamedPests, distinctPests, type PestRegistrationRow } from '@/utils/targetPests';

interface TargetPestPickerProps {
  /** Every registration for the chosen situation. */
  registrations: PestRegistrationRow[];
  loading?: boolean;
  /** Selected pest keys (see `pestKey`). */
  value: string[];
  onChange: (keys: string[]) => void;
}

/**
 * Choose what the spray is for before choosing what goes in the tank. The
 * options come from the situation's own registrations, so every pest offered
 * has at least one product registered against it on this crop.
 */
function TargetPestPicker({ registrations, loading, value, onChange }: TargetPestPickerProps) {
  const [query, setQuery] = useState('');

  const pests = useMemo(() => distinctPests(registrations), [registrations]);
  const unnamed = useMemo(() => countUnnamedPests(registrations), [registrations]);
  const labelFor = (key: string) => pests.find(p => p.key === key)?.label ?? key;

  const q = query.trim().toLowerCase();
  const shown = q ? pests.filter(p => p.label.toLowerCase().includes(q)) : pests;

  const toggle = (key: string) =>
    onChange(value.includes(key) ? value.filter(k => k !== key) : [...value, key]);

  if (loading) {
    return <p className="text-sm text-neutral-400">Loading registered pests…</p>;
  }
  if (!pests.length) {
    return (
      <p className="text-sm text-neutral-500">
        No target pests are recorded against this situation's registrations — pick the chemical directly.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary-600 px-2.5 py-1 text-left text-xs font-medium text-white hover:bg-primary-700"
              aria-label={`Remove ${labelFor(key)}`}
            >
              <span className="break-words">{labelFor(key)}</span>
              <X className="h-3 w-3 shrink-0" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange([])}
            className="px-1 text-xs text-neutral-500 hover:text-neutral-700"
          >
            Clear
          </button>
        </div>
      )}

      <div className="rounded-lg border border-neutral-300 bg-white">
        <div className="flex items-center gap-2 border-b border-neutral-100 px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-neutral-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search pests and diseases…"
            aria-label="Search target pests"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder-neutral-400"
          />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {shown.length === 0 ? (
            <p className="p-3 text-center text-sm text-neutral-500">No registered pest matches “{query}”</p>
          ) : (
            shown.map(p => {
              const active = value.includes(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => toggle(p.key)}
                  aria-pressed={active}
                  className={`flex w-full items-center gap-2 border-b border-neutral-50 px-3 py-2 text-left text-sm last:border-b-0 ${
                    active ? 'bg-primary-50 text-primary-800' : 'text-neutral-800 hover:bg-neutral-50'
                  }`}
                >
                  <Bug className={`h-4 w-4 shrink-0 ${active ? 'text-primary-600' : 'text-neutral-400'}`} />
                  <span className="min-w-0 flex-1 break-words">{p.label}</span>
                  <span className="shrink-0 text-xs text-neutral-400">
                    {p.chemicalCount} product{p.chemicalCount !== 1 ? 's' : ''}
                  </span>
                  {active && <Check className="h-4 w-4 shrink-0 text-primary-600" />}
                </button>
              );
            })
          )}
        </div>
      </div>

      {value.length > 0 && unnamed > 0 && (
        <p className="text-xs text-neutral-400">
          {unnamed} registration{unnamed !== 1 ? 's' : ''} with no target pest recorded {unnamed !== 1 ? 'are' : 'is'} hidden
          while filtering.
        </p>
      )}
    </div>
  );
}

export default TargetPestPicker;
